import { useEffect, useState, useRef } from 'react'
import styles from './Servers.module.css'

// ── ICONS ──────────────────────────────────────────────────────────────────────
const RefreshIcon = ({ spin }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ animation: spin ? 'spin 0.7s linear infinite' : 'none' }}>
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
)

const CheckCircle = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
)

const AlertCircle = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)

const LoaderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ animation: 'spin 1s linear infinite' }}>
    <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
    <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
  </svg>
)

const UsersIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)

const GlobeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)

const ShieldIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)

// ── ANIMATED COUNTER ───────────────────────────────────────────────────────────
function Counter({ value, duration = 600 }) {
  const [display, setDisplay] = useState(0)
  const prev = useRef(0)
  useEffect(() => {
    const target = Number(value) || 0
    const start = prev.current
    if (start === target) return
    const t0 = performance.now()
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setDisplay(Math.round(start + (target - start) * eased))
      if (p < 1) requestAnimationFrame(tick)
      else prev.current = target
    }
    requestAnimationFrame(tick)
  }, [value, duration])
  return <>{display}</>
}

// ── MINI SPARKLINE ─────────────────────────────────────────────────────────────
function Sparkline({ active, status }) {
  const bars = 10
  return (
    <div className={styles.sparkline}>
      {Array.from({ length: bars }).map((_, i) => {
        const h = status === 'online'
          ? 30 + Math.abs(Math.sin(i * 1.3 + (active || 0) * 0.1)) * 60
          : 15
        return (
          <div
            key={i}
            className={styles.sparkBar}
            style={{
              height: `${h}%`,
              animationDelay: `${i * 150}ms`,
              opacity: status === 'online' ? 0.6 + (i / bars) * 0.4 : 0.2,
            }}
          />
        )
      })}
    </div>
  )
}

// ── UPTIME RING ────────────────────────────────────────────────────────────────
function UptimeRing({ status }) {
  const pct = status === 'online' ? 99 : status === 'checking' ? 50 : 0
  const r = 18, circ = 2 * Math.PI * r
  const dash = (pct / 100) * circ
  const color = status === 'online' ? 'var(--green)' : status === 'checking' ? 'var(--yellow)' : 'var(--red)'
  return (
    <div className={styles.ringWrap}>
      <svg width="48" height="48" viewBox="0 0 48 48" className={styles.ringSvg}>
        <circle cx="24" cy="24" r={r} fill="none" stroke="var(--border)" strokeWidth="3"/>
        <circle cx="24" cy="24" r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${dash} ${circ}`}
          strokeDashoffset={circ / 4}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1.2s ease', filter: `drop-shadow(0 0 4px ${color})` }}
        />
      </svg>
      <span className={styles.ringPct} style={{ color }}>{pct}%</span>
    </div>
  )
}

// ── SERVER CARD ────────────────────────────────────────────────────────────────
function ServerCard({ data, index }) {
  const { server, status, ping, active, checkedAt } = data
  const count = active?.count ?? 0
  const sessions = ping?.activeSessions ?? 0

  return (
    <div
      className={`${styles.card} ${styles[status]}`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* TOP ACCENT LINE */}
      <div className={styles.cardAccent} />

      {/* HEADER */}
      <div className={styles.cardHeader}>
        <div className={styles.serverMeta}>
          <div className={styles.serverLabel}>{server.label}</div>
          <div className={styles.serverRegion}>
            <GlobeIcon /> {server.region || 'Global'}
          </div>
        </div>

        <div className={`${styles.statusPill} ${styles[status]}`}>
          {status === 'online'   && <><CheckCircle /> Online</>}
          {status === 'offline'  && <><AlertCircle /> Offline</>}
          {status === 'checking' && <><LoaderIcon /> Checking</>}
        </div>
      </div>

      {/* METRICS ROW */}
      <div className={styles.metricsRow}>
        <div className={styles.metricBlock}>
          <div className={styles.metricNum} style={{ color: count > 0 ? 'var(--accent)' : 'var(--muted)' }}>
            <Counter value={count} />
          </div>
          <div className={styles.metricLbl}>Active Bots</div>
        </div>

        <div className={styles.metricDivider} />

        <div className={styles.metricBlock}>
          <div className={styles.metricNum} style={{ color: sessions > 0 ? 'var(--green)' : 'var(--muted)' }}>
            <Counter value={sessions} />
          </div>
          <div className={styles.metricLbl}>Sessions</div>
        </div>

        <div className={styles.metricDivider} />

        <div className={styles.metricBlock}>
          <UptimeRing status={status} />
          <div className={styles.metricLbl}>Uptime</div>
        </div>

        <div className={styles.metricDivider} />

        {/* SPARKLINE */}
        <div className={styles.metricBlock} style={{ flex: 2 }}>
          <Sparkline active={count} status={status} />
          <div className={styles.metricLbl}>Load</div>
        </div>
      </div>

      {/* SESSION COUNT BAR — shows count without exposing numbers */}
      {count > 0 && status === 'online' && (
        <div className={styles.sessionBar}>
          <div className={styles.sessionBarTop}>
            <span className={styles.sessionBarLabel}>
              <UsersIcon /> Session capacity
            </span>
            <span className={styles.sessionBarCount}>{count} active</span>
          </div>
          <div className={styles.sessionBarTrack}>
            <div
              className={styles.sessionBarFill}
              style={{ width: `${Math.min((count / 20) * 100, 100)}%` }}
            />
          </div>
          <div className={styles.sessionPrivacyNote}>
            <ShieldIcon /> Session details are private
          </div>
        </div>
      )}

      {/* PING STATUS MESSAGE */}
      {ping?.message && (
        <div className={styles.pingBadge}>
          <span className={styles.pingDot} />
          {ping.message}
        </div>
      )}

      {/* FOOTER */}
      <div className={styles.cardFooter}>
        <span className={styles.footerId}>#{String(server.id).padStart(2, '0')}</span>
        {checkedAt && (
          <span className={styles.footerTime}>
            Synced {checkedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  )
}

// ── PAGE ───────────────────────────────────────────────────────────────────────
export default function Servers({ stats, refresh, refreshing, lastRefresh }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => { setTimeout(() => setVisible(true), 60) }, [])

  const onlineCount  = stats.filter((s) => s.status === 'online').length
  const offlineCount = stats.filter((s) => s.status === 'offline').length
  const totalBots    = stats.reduce((a, s) => a + (s.active?.count ?? 0), 0)
  const allOnline    = onlineCount === stats.length && stats.length > 0

  return (
    <div className={`${styles.page} ${visible ? styles.visible : ''}`}>

      {/* HEADER */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.breadcrumb}>
            <span>Network</span>
            <span className={styles.sep}>/</span>
            <span className={styles.current}>Servers</span>
          </div>
          <h2 className={styles.title}>Infrastructure</h2>
          <p className={styles.desc}>
            Live health across the Saviya-X distributed network.
            Auto-syncs every 30 seconds.
          </p>
        </div>
        <button className={styles.refreshBtn} onClick={refresh} disabled={refreshing}>
          <RefreshIcon spin={refreshing} />
          {refreshing ? 'Syncing…' : 'Sync Now'}
        </button>
      </div>

      {/* SUMMARY BANNER */}
      <div className={`${styles.banner} ${allOnline ? styles.bannerGreen : offlineCount > 0 ? styles.bannerRed : styles.bannerYellow}`}>
        <div className={styles.bannerDot} />
        <span className={styles.bannerText}>
          {allOnline
            ? 'All systems operational'
            : offlineCount === stats.length
            ? 'All servers unreachable'
            : `${offlineCount} server${offlineCount > 1 ? 's' : ''} degraded — ${onlineCount} healthy`}
        </span>
        {lastRefresh && (
          <span className={styles.bannerTime}>Last sync {lastRefresh.toLocaleTimeString()}</span>
        )}
      </div>

      {/* STATS STRIP */}
      <div className={styles.statsStrip}>
        {[
          { label: 'Online',       val: onlineCount,  color: 'var(--green)',  sub: 'servers' },
          { label: 'Offline',      val: offlineCount, color: offlineCount > 0 ? 'var(--red)' : 'var(--muted)', sub: 'servers' },
          { label: 'Active Bots',  val: totalBots,    color: 'var(--accent)', sub: 'across network' },
          { label: 'Total Nodes',  val: stats.length, color: 'var(--text)',   sub: 'registered' },
        ].map((s, i) => (
          <div key={s.label} className={styles.statItem} style={{ animationDelay: `${i * 60}ms` }}>
            <div className={styles.statVal} style={{ color: s.color }}>
              <Counter value={s.val} />
            </div>
            <div className={styles.statLabel}>{s.label}</div>
            <div className={styles.statSub}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* SERVER GRID */}
      <div className={styles.grid}>
        {stats.map((s, i) => (
          <ServerCard key={s.server.id} data={s} index={i} />
        ))}
      </div>

    </div>
  )
}
